import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { ApiHost } from "../utils/ApiConfig";
import { formatCertificateNumber } from "../utils/utils";

import logger from "../utils/logger";
/**
 * Transformuje pojedynczą aplikację z API na format dla tabeli
 */
export const transformApplicationResponse = (apiApp) => {
  return {
    Numer: apiApp.Number || "",
    Nazwa: apiApp.Name || "",
    Eksporter: apiApp.ExporterName || "Brak danych",
    NIP: apiApp.ExporterNIP || "Brak NIP",
    Status: apiApp.StatusName || "",
    "Nr świadectwa": formatCertificateNumber(apiApp.CertificateNumber),
    Obsługuje: apiApp.OperatorName === "- -" ? "-" : apiApp.OperatorName || "-",
    Izba: apiApp.ChamberName || "Brak przypisania",
    "Data utworzenia": apiApp.CreateDate || "",
    Złożono: apiApp.SentDate || "",
    Aktualizacja: apiApp.LastUpdate || "",
    Action: apiApp.Id,
    FirstApplicantResponseStep: apiApp.FirstApplicantResponseStep ?? null,
    HasUnreadCaseMessage: Boolean(apiApp.HasUnreadCaseMessage),
    _id: apiApp.Id,
  };
};

export const transformApplicationsList = (apiApps) => {
  if (!Array.isArray(apiApps)) return [];
  return apiApps.map(transformApplicationResponse);
};

export const reverseTransform = (uiApp) => {
  return {
    Number: uiApp.Numer,
    Name: uiApp.Nazwa,
    ExporterName: uiApp.Eksporter,
    ExporterNIP: uiApp.NIP,
    StatusName: uiApp.Status,
    CertificateNumber: uiApp["Nr świadectwa"],
    OperatorName: uiApp.Obsługuje,
    ChamberName: uiApp.Izba,
    CreateDate: uiApp["Data utworzenia"],
    SentDate: uiApp.Złożono,
    LastUpdate: uiApp.Aktualizacja,
    Id: uiApp._id,
  };
};

function useApplicationList({
  mode = "Client",
  paymentMode = false,
  shippingMode = false,
}) {
  const { accessToken, userId, chamberId } = useAuth();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needsRefresh, setNeedsRefresh] = useState(0);

  const getApiMethod = useCallback(() => {
    switch (mode) {
      case "operator":
        return "/application/assigned";
      case "coordinator":
      case "shipping":
        return "/application/assigned_to_chamber";
      case "user":
      default:
        return "/application/list";
    }
  }, [mode]);

  const fetchApplications = useCallback(async () => {
    if (!userId || !accessToken) {
      setError("Brak wymaganych danych autoryzacji");
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const apiMethod = getApiMethod();

      const response = await axios.post(
        ApiHost + apiMethod,
        {
          user_id: userId,
          chamber_id: chamberId,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        },
      );

      if (!isMounted) return;

      if (response.status === 200 && Array.isArray(response.data)) {
        const transformed = response.data.map((app) =>
          transformApplicationResponse(app),
        );
        setApplications(transformed);
      }
    } catch (error) {
      if (!isMounted) return;
      logger.error("[useApplicationList] Błąd pobierania:", error);
      setError(error.message || "Błąd pobierania listy aplikacji");
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [userId, accessToken, chamberId, getApiMethod]);

  const refresh = useCallback(() => {
    setNeedsRefresh((prev) => prev + 1);
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [
    mode,
    paymentMode,
    shippingMode,
    userId,
    accessToken,
    fetchApplications,
    needsRefresh,
  ]);

  const value = {
    applications,
    loading,
    error,
    refresh,
    count: applications.length,
  };

  return value;
}

export default useApplicationList;
